import type { AgentDraft } from "@nemo/agent-schema";
import { useState } from "react";
import type { AgentDetail, Issue } from "../api.js";
import type { FieldErrors } from "../draft.js";
import { Api } from "../api.js";
import { useI18n } from "../i18n.js";
import { BlockedSitesPanel } from "./BlockedSites.js";
import { CheckSite } from "./CheckSite.js";
import { ReadyPanel } from "../readiness/ReadyPanel.js";
import { runtimeConfig } from "../runtime-config.js";
import { Badge, Field, ListEditor, Section, TextInput, Toggle } from "./ui.js";

const THEMES = [
  { id: "purple", color: "#6a55f0" },
  { id: "green", color: "#15803d" },
  { id: "navy", color: "#1e3a8a" },
  { id: "orange", color: "#c2410c" },
  { id: "teal", color: "#0f766e" },
  { id: "pink", color: "#be185d" },
] as const;

type Web = NonNullable<NonNullable<AgentDraft["deployment"]>["website"]>;

/**
 * One line, whatever the agent looks like. Everything that can change later (mode, theme, texts, models,
 * knowledge, published version) is read from the server by agent id, and the script is served from a fixed,
 * unversioned URL that always delivers the current widget. So editing or republishing never changes this code.
 * The script finds the API by itself (the hosting writes the address into widget.js); an old snippet that still
 * carries data-api keeps working. The PAGE decides how the chat shows: a box with id "nemo-chat" means inline.
 */
export function embedCode(agentId: string, cfg = runtimeConfig()): string {
  return `<script src="${cfg.widgetUrl}" data-agent-id="${agentId}" async></script>`;
}

/** The public chat page of an agent: on the settings app's own address, which also serves widget.js. */
export function hostedChatUrl(agentId: string, cfg = runtimeConfig()): string {
  return `${new URL(cfg.widgetUrl).origin}/chat/${agentId}`;
}

export const INLINE_NOTE = '<div id="nemo-chat" style="height:520px"></div>';

const PLATFORMS = ["wordpress", "wix", "squarespace", "shopify", "html", "gtm"] as const;

interface Props {
  agentId: string;
  form: AgentDraft;
  update: (fn: (f: AgentDraft) => AgentDraft) => void;
  errors: FieldErrors;
  detail: AgentDetail;
  saving: boolean;
  trainingBusy: boolean;
  onPublish: () => Promise<void>;
  onUnpublish: () => Promise<void>;
  publishIssues: Issue[];
  publishError: string | null;
  busy: boolean;
  /** With these, the step lists sites that tried to load the agent without being allowed, with Allow / Dismiss. */
  api?: Api;
  saveNow?: () => Promise<void>;
  onDomainsChanged?: () => Promise<void>;
  /** For the "Ready to publish?" panel: open the preview chat, and jump to the step that holds a setting. */
  onOpenPreview?: () => void;
  onGo?: (step: "plan" | "train" | "user-data") => void;
}

export function LaunchStep(p: Props) {
  const web: Web = { mode: "floating", position: "right", style: 1, theme: "purple", fontSize: "small", useWebsiteFonts: true, language: "en", allowedDomains: [], ...(p.form.deployment?.website ?? {}) } as Web;
  const setWeb = (patch: Partial<Web>) => p.update((f) => ({ ...f, deployment: { website: { ...web, ...patch } } }));
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const published = p.detail.status === "published";
  const code = embedCode(p.agentId);

  const [linkCopied, setLinkCopied] = useState(false);
  const chatLink = hostedChatUrl(p.agentId);
  const copyText = async (text: string, done: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      done(true);
      setTimeout(() => done(false), 2000);
    } catch {
      /* the text is selectable */
    }
  };
  const copy = () => copyText(code, setCopied);

  return (
    <div className="step">
      <Section title="Website chat" help="Where and how the chat window appears on your site.">
        <div className="grid2">
          <Field label="When the page has no chat box" htmlFor="mode" help="If your page has a box with id nemo-chat, the chat always appears inside it. This is only used when it does not.">
            <select id="mode" value={web.mode} onChange={(e) => setWeb({ mode: e.target.value as Web["mode"] })}>
              <option value="floating">Floating bubble (recommended)</option>
              <option value="inline">Inline (only if the page has a box)</option>
            </select>
          </Field>
          {web.mode === "floating" && (
            <Field label="Bubble position" htmlFor="position">
              <select id="position" value={web.position} onChange={(e) => setWeb({ position: e.target.value as Web["position"] })}>
                <option value="right">Bottom right</option>
                <option value="left">Bottom left</option>
              </select>
            </Field>
          )}
          <Field label="Chat language" htmlFor="language" help="The language of buttons and labels. The agent replies in the visitor's language.">
            <select id="language" value={web.language} onChange={(e) => setWeb({ language: e.target.value as Web["language"] })}>
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </Field>
          <Field label="Style" htmlFor="style">
            <select id="style" value={web.style} onChange={(e) => setWeb({ style: Number(e.target.value) as Web["style"] })}>
              <option value={1}>1 · Rounded</option>
              <option value={2}>2 · Square</option>
              <option value={3}>3 · Soft, with labelled bubble</option>
            </select>
          </Field>
        </div>
        <Field label="Theme color">
          <div className="swatches" role="radiogroup" aria-label="Theme color">
            {THEMES.map((t) => (
              <button key={t.id} type="button" role="radio" aria-checked={web.theme === t.id} aria-label={t.id} className={`swatch ${web.theme === t.id ? "on" : ""}`} style={{ background: t.color }} onClick={() => setWeb({ theme: t.id })} />
            ))}
          </div>
        </Field>
        <TextInput label="Brand name" max={60} value={web.brandName ?? ""} onChange={(v) => setWeb({ brandName: v || undefined })} error={p.errors["deployment.website.brandName"]} help="Shown under the agent's name. Optional." />
        <Toggle
          label="Let visitors speak their messages"
          checked={web.voiceInput ?? false}
          onChange={(v) => setWeb({ voiceInput: v, ...(v ? {} : { voiceAutoSend: false }) })}
          help="Adds a microphone button to the chat. It uses the visitor's browser (Chrome, Edge and Safari; not Firefox). In Chrome and Edge the browser may send the audio to its speech service to turn it into text, so visitors see a short notice before their first use. Nemo does not record or store the voice. For your privacy policy: the audio is handled by the visitor's browser maker (Google, Microsoft or Apple), not by Nemo. It does not work on pages without https, and often not inside the built-in browsers of apps such as Instagram or Facebook (visitors there are told to open the page in Chrome or Safari). If the microphone is blocked, visitors are shown how to allow it, and can always type. Off by default."
        />
        {web.voiceInput && (
          <Toggle
            label="Send automatically when they stop speaking"
            checked={web.voiceAutoSend ?? false}
            onChange={(v) => setWeb({ voiceAutoSend: v })}
            help="Off is recommended: the words appear in the message box so the visitor can check them before sending."
          />
        )}
        <Toggle label={t("src.label")} checked={web.attribution?.enabled ?? true} onChange={(v) => setWeb({ attribution: { enabled: v } })} help={t("src.help")} />
        {(web.attribution?.enabled ?? true) && <p className="hint">{t("src.notice")}</p>}
        <Toggle label="Use my website's fonts" checked={web.useWebsiteFonts ?? true} onChange={(v) => setWeb({ useWebsiteFonts: v })} />
        {!web.useWebsiteFonts && (
          <div className="grid2">
            <TextInput label="Font family" value={web.fontFamily ?? ""} onChange={(v) => setWeb({ fontFamily: v || undefined })} placeholder="Georgia, serif" />
            <Field label="Font size" htmlFor="fontsize">
              <select id="fontsize" value={web.fontSize} onChange={(e) => setWeb({ fontSize: e.target.value as Web["fontSize"] })}>
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </Field>
          </div>
        )}
      </Section>

      <Section title="Allowed domains" help="The chat only works on these websites. Use example.com, or *.example.com for all subdomains. A single * or *.com is not allowed.">
        <ListEditor label="Domains" required items={web.allowedDomains ?? []} onChange={(v) => setWeb({ allowedDomains: v })} placeholder="www.example.com" addLabel="Add domain" errors={p.errors} errorPrefix="deployment.website.allowedDomains" />
      </Section>

      {p.api && (
        <Section title={t("blocked.title")}>
          <BlockedSitesPanel api={p.api} agentId={p.agentId} saveNow={p.saveNow ?? (async () => {})} onChanged={p.onDomainsChanged ?? (async () => {})} />
        </Section>
      )}

      <Section title={t("link.title")}>
        <Toggle label={t("link.toggle")} checked={web.publicChat ?? false} onChange={(v) => setWeb({ publicChat: v })} help={t("link.help")} />
        {web.publicChat && (
          <>
            <pre className="code" tabIndex={0} aria-label={t("link.label")}>{chatLink}</pre>
            <button className="btn secondary" onClick={() => void copyText(chatLink, setLinkCopied)}>{linkCopied ? t("embed.copied") : t("link.copy")}</button>
            <p className="hint">{published ? t("link.frames") : t("link.publish")}</p>
          </>
        )}
      </Section>

      <Section title="Publish">
        <ReadyPanel api={p.api} agentId={p.agentId} form={p.form} onOpenPreview={p.onOpenPreview} onGo={p.onGo} />
        <div className="row between">
          <div>
            <Badge tone={published ? "ok" : p.detail.status === "unpublished" ? "warn" : "muted"}>{published ? `Published (version ${p.detail.publishedVersion})` : p.detail.status === "unpublished" ? "Unpublished" : "Draft"}</Badge>
            {published && p.detail.hasUnpublishedChanges && <span className="hint"> You have changes that are not live yet.</span>}
          </div>
          <div className="row">
            {published && (
              <button className="btn secondary" onClick={() => void p.onUnpublish()} disabled={p.busy}>
                Unpublish
              </button>
            )}
            <button className="btn primary" onClick={() => void p.onPublish()} disabled={p.busy || p.saving || p.trainingBusy} aria-describedby="publish-state">
              {p.busy ? "Publishing…" : published ? "Publish changes" : "Publish"}
            </button>
          </div>
        </div>
        <p className="hint" id="publish-state" aria-live="polite">
          {p.trainingBusy ? "Training is still running. You can publish when it finishes." : p.saving ? "Saving your changes…" : ""}
        </p>
        {p.publishError && <div className="banner warn" role="alert">{p.publishError}</div>}
        {p.publishIssues.length > 0 && (
          <div className="issues" role="alert">
            <strong>Fix these before publishing:</strong>
            <ul>
              {p.publishIssues.map((i, n) => (
                <li key={n}>
                  <span className="issue-path">{labelFor(i.path)}</span> {i.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!p.publishIssues.length && !p.detail.readiness.ready && !published && (
          <div className="issues">
            <strong>Not ready to publish yet:</strong>
            <ul>
              {p.detail.readiness.issues.map((i, n) => (
                <li key={n}>
                  <span className="issue-path">{labelFor(i.path)}</span> {i.message}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <Section title={t("embed.title")} help={published ? t("embed.helpPublished") : t("embed.helpDraft")}>
        <pre className="code" tabIndex={0} aria-label={t("embed.code")}>{code}</pre>
        <button className="btn secondary" onClick={copy}>{copied ? t("embed.copied") : t("embed.copy")}</button>
        <p className="hint">{t("embed.never")}</p>
        <p className="hint" data-testid="viewport-hint">{t("embed.viewport")}</p>
        <h3 className="subhead">{t("embed.where")}</h3>
        {PLATFORMS.map((id) => (
          <details key={id} className="where">
            <summary>{t(`embed.${id}`)}</summary>
            <p>{t(`embed.${id}.how`)}</p>
          </details>
        ))}
        <details className="where">
          <summary>{t("embed.inline")}</summary>
          <p>{t("embed.inline.how")}</p>
          <pre className="code" tabIndex={0} aria-label={t("embed.inline.label")}>{INLINE_NOTE}</pre>
        </details>
      </Section>

      {p.api && (
        <Section title={t("checksite.title")}>
          <CheckSite api={p.api} agentId={p.agentId} onAllow={(entries) => setWeb({ allowedDomains: [...new Set([...(web.allowedDomains ?? []), ...entries])] })} />
        </Section>
      )}

      <Section title={t("fix.title")}>
        <p className="help">{t("fix.intro")}</p>
        <ul className="plain">
          {FIX_ITEMS.map((k) => (
            <li key={k}>
              <strong>{t(`fix.${k}.q` as never)}</strong> {t(`fix.${k}.a` as never)}
            </li>
          ))}
        </ul>
        <p className="help">{t("fix.doctor")}</p>
        <pre className="code" tabIndex={0} aria-label={t("fix.lift.label")}>{LIFT_NOTE}</pre>
      </Section>

      <Section title="Other channels">
        <ul className="channels">
          {["WhatsApp", "Facebook and Instagram", "Slack"].map((c) => (
            <li key={c} className="row between">
              <span>{c}</span>
              <Badge tone="muted">Coming soon</Badge>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

const FIX_ITEMS = ["domain", "place", "published", "plan", "blocker", "csp", "banner"] as const;
const LIFT_NOTE = `<script src="…/widget.js" data-agent-id="…" data-bottom-offset="80" async></script>`;

const LABELS: Record<string, string> = {
  name: "Agent name:",
  creatorName: "Creator name:",
  persona: "Job, skills and personality:",
  responseInstructions: "Response instructions:",
  greetings: "Greeting message:",
  "greetings.0": "Greeting message:",
  "model.generative": "Generative model:",
  "model.embedding": "Embedding model:",
  model: "Models:",
  "deployment.website.allowedDomains": "Allowed domains:",
  "leadCapture.fields": "User data fields:",
  leadCapture: "User data:",
  strictness: "Strictness:",
  conversationStarters: "Conversation starters:",
  "knowledge.sources": "Training:",
};
const labelFor = (path: string) => LABELS[path] ?? (path ? `${path}:` : "");
